import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { SideNav } from './SideNav';
import { userService } from '../services/userService';
import { 
  PageLayout, 
  Container, 
  Card, 
  H2, 
  Body1, 
  Body2
} from './ui';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Settings() {
  const { user, sideNavLocked, setSideNavLocked } = useAuth();
  
  // Pricing data mapping (matches PaymentStep.js)
  const pricingData = {
    'Monthly Plan': { 
      GBP: { price: 8.00, currency: '£' },
      USD: { price: 11.00, currency: '$' }
    },
    'Quarterly Plan': { 
      GBP: { price: 18.00, currency: '£', monthlyEquivalent: 6.00 },
      USD: { price: 25.00, currency: '$', monthlyEquivalent: 8.33 }
    },
    // Backend stores full names with "Elena" prefix
    'Elena Monthly Plan': { 
      GBP: { price: 8.00, currency: '£' },
      USD: { price: 11.00, currency: '$' }
    },
    'Elena Quarterly Plan': { 
      GBP: { price: 18.00, currency: '£', monthlyEquivalent: 6.00 },
      USD: { price: 25.00, currency: '$', monthlyEquivalent: 8.33 }
    }
  };
  
  // State for subscription management
  const [subscriptionData, setSubscriptionData] = useState({
    plan: 'Free',
    status: 'onboarding',
    nextChargeDate: '',
    cancelAtPeriodEnd: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [error, setError] = useState(null);

  // Sync subscription status from Stripe (for development when webhooks don't work)
  const syncSubscriptionFromStripe = async () => {
    try {
      setLoadingDetails(true);
      setError(null);
      
      console.log('🔄 Syncing subscription from Stripe...');
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // First sync the subscription status
      const syncResponse = await fetch(`${baseURL}/api/payment/sync-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (syncResponse.ok) {
        const syncResult = await syncResponse.json();
        console.log('✅ Subscription synced:', syncResult);
      } else {
        console.log('ℹ️ Sync may have failed, but continuing to fetch details...');
      }

      // Then fetch the updated subscription details
      const response = await fetch(`${baseURL}/api/payment/subscription-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const details = await response.json();
        console.log('📊 Subscription details received:', details);
        
        // Simplified: Use nextChargeDate from backend for all cases
        let nextChargeDate = null;
        if (details.nextChargeDate) {
          nextChargeDate = new Date(details.nextChargeDate).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }

        setSubscriptionData({
          plan: details.billingPlan || 'Free',
          status: details.journeyStage || 'onboarding',
          currency: details.currency || 'GBP',
          nextChargeDate: nextChargeDate,
          cancelAtPeriodEnd: details.cancelAtPeriodEnd || false,
          isTrialing: details.isTrialing || false
        });
      } else {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        setError(`Failed to load subscription details: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Error syncing subscription:', err);
      setError('Failed to sync subscription. Please try again.');
      
      // Fallback to user data
      if (user) {
        console.log('🔄 Falling back to user data');
        setSubscriptionData({
          plan: user.billingPlan || 'Free',
          status: user.journeyStage || 'onboarding',
          nextChargeDate: '',
          cancelAtPeriodEnd: user.cancelAtPeriodEnd || false
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  // Keep the old function for backward compatibility
  const fetchSubscriptionDetails = syncSubscriptionFromStripe;

  // Initialize subscription data
  useEffect(() => {
    if (user && user._id) {
      console.log('🔄 Fetching subscription details for user:', user.email);
      fetchSubscriptionDetails();
    }
  }, [user]);

  // Auto-refresh when returning from Stripe portal
  useEffect(() => {
    const handleFocus = () => {
      // Only refresh if we have a user and it's been more than 5 seconds since last fetch
      if (user && user._id && !loadingDetails) {
        console.log('🔄 Page focused - refreshing subscription data...');
        fetchSubscriptionDetails();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, loadingDetails]);

  // Get user's first name
  const getUserFirstName = () => {
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    return 'User';
  };

  // Get plan display text and pricing
  const getPlanDisplay = () => {
    const plan = subscriptionData.plan;
    const status = subscriptionData.status;
    
    // Handle different states for a paid-only app
    if (status === 'onboarding' || !plan || plan === 'Free') {
      return {
        name: 'No Plan Selected',
        pricing: 'Please complete subscription setup'
      };
    }
    
    if (status === 'expired') {
      return {
        name: 'Subscription Expired',
        pricing: 'Resubscribe to continue using Elena'
      };
    }
    
    // Use currency from subscription data or user data, default to GBP
    const currency = subscriptionData.currency || user?.currency || 'GBP';
    const planData = pricingData[plan]?.[currency];
    
    if (!planData) {
      return {
        name: plan,
        pricing: 'Premium features enabled'
      };
    }
    
    // Determine if it's a quarterly plan
    const isQuarterly = plan.toLowerCase().includes('quarterly');
    
    let pricing;
    if (isQuarterly && planData.monthlyEquivalent) {
      pricing = `${planData.currency}${planData.price}/quarter (${planData.currency}${planData.monthlyEquivalent}/month)`;
    } else {
      pricing = `${planData.currency}${planData.price}/${isQuarterly ? 'quarter' : 'month'}`;
    }
    
    return {
      name: plan,
      pricing: pricing
    };
  };



  // Handle Stripe Customer Portal
  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${baseURL}/api/payment/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to open subscription management');
      }
    } catch (err) {
      console.error('Error opening subscription management:', err);
      setError('Failed to open subscription management. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get status display info
  const getStatusDisplay = () => {
    switch (subscriptionData.status) {
      case 'trial':
        return {
          text: 'Free Trial Active',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500 bg-opacity-20',
          description: '7-day trial period active'
        };
      case 'active':
        return {
          text: 'Active Subscription',
          color: 'text-green-400',
          bgColor: 'bg-green-500 bg-opacity-20',
          description: 'Subscription active and billing normally'
        };
      case 'cancelling':
        return {
          text: 'Cancelling at Period End',
          color: 'text-orange-400',
          bgColor: 'bg-orange-500 bg-opacity-20',
          description: 'Will cancel at end of current billing period'
        };
      case 'expired':
        return {
          text: 'Subscription Ended',
          color: 'text-red-400',
          bgColor: 'bg-red-500 bg-opacity-20',
          description: 'Subscription has ended'
        };
      default:
        return {
          text: 'Setup Required',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500 bg-opacity-20',
          description: 'Complete your subscription setup to access Elena'
        };
    }
  };

  return (
    <>
      <SideNav />
      <div className={`transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          <div className={`p-6 ${!sideNavLocked ? 'pl-20' : ''}`}>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
              <p className="text-gray-400">Manage your account settings and billing information</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg text-red-200">
                {error}
              </div>
            )}

            {/* Account Statistics Section */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl mb-6">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-4">Account Statistics</h2>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    {/* Document Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Documents Uploaded</label>
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {user?.docCount || 0} / 8
                        </div>
                      </div>
                    </div>

                    {/* Job Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Jobs Tracked</label>
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="text-white">
                          {user?.jobCount || 0} / 50
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Completeness */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Profile Completeness</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="text-white">
                          {user?.profileCompleteness || 0}% Complete
                        </div>
                        <div className="w-32 bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${user?.profileCompleteness || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Management Section */}
            <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl">
              <div className="p-6">
                <div className="space-y-6">
                  {/* Section Header */}
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-4">Subscription Management</h2>
                  </div>



                  {/* Subscription Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Current Status</label>
                    {loadingDetails ? (
                      <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                        <div className="flex items-center">
                          <svg className="animate-spin w-5 h-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div className="text-gray-400">Loading subscription details...</div>
                        </div>
                      </div>
                    ) : (
                      <div className={`bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3 ${getStatusDisplay().bgColor}`}>
                        <div className={`font-medium ${getStatusDisplay().color}`}>
                          {getStatusDisplay().text}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          {getStatusDisplay().description}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Current Plan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Current Plan</label>
                    <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-3">
                      <div className="text-white font-medium">
                        {getPlanDisplay().name}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        {getPlanDisplay().pricing}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-400">
                      💡 For billing dates and payment history, use "Manage Subscription" below.
                    </div>
                  </div>



                  {/* Trial Info (if in trial) */}
                  {subscriptionData.status === 'trial' && (
                    <div className="bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-30 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <div className="text-blue-300 font-medium mb-3">🎉 7-day free trial active</div>
                          <div className="text-blue-300 text-sm">
                            Your {subscriptionData.plan} subscription will continue automatically. Use "Manage Subscription" below to view billing details.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subscription Actions */}
                  <div className="pt-4 border-t border-gray-600 border-opacity-30">
                    {subscriptionData.status === 'trial' || subscriptionData.status === 'active' || subscriptionData.status === 'cancelling' ? (
                      <div className="space-y-3">
                        <button 
                          onClick={handleManageSubscription}
                          disabled={loading}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Opening...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              Manage Subscription
                            </>
                          )}
                        </button>
                        <p className="text-sm text-gray-400">
                          Opens Stripe's secure portal to manage your subscription, payment methods, and download invoices.
                        </p>
                      </div>
                    ) : subscriptionData.status === 'expired' ? (
                      <div className="space-y-3">
                        <div className="text-red-300 text-sm">
                          Your subscription has ended. Resubscribe to continue using Elena.
                        </div>
                        <button 
                          onClick={() => window.location.href = '/resubscribe'}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                        >
                          Resubscribe
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-yellow-300 text-sm">
                          Complete your subscription setup to access Elena's premium features.
                        </div>
                        <button 
                          onClick={() => window.location.href = '/onboarding'}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                        >
                          Complete Setup
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageLayout>
      </div>
    </>
  );
}

export default Settings; 